import { z } from 'zod';

import { TripCalendarEventSchema } from './trip_calendar_event';

/**
 * A flight's card on the rebooked e-mail (#7099): route, times, length, stops and the airline
 * code. Every field optional — producer and conversation deploy separately, and a field the
 * voucher does not carry drops its piece of the card.
 */
const RebookedFlightCardSchema = z.object({
  /** Offset-less wall clock of the last landing. */
  arriveLocal: z.string().nullable().optional(),
  /** Real elapsed minutes from the first departure to the last arrival. */
  durationMinutes: z.number().int().positive().nullable().optional(),
  /** Stops between the first boarding and the last landing. */
  stops: z.number().int().min(0).nullable().optional(),
  /** The airline's booking code, as the traveler hands it to the counter. Display only. */
  locator: z.string().nullable().optional(),
});

/** The flight the rebooking replaced, as its (still emitted) voucher held it. */
const PreviousFlightSchema = RebookedFlightCardSchema.extend({
  carrier: z.string().nullable(),
  flightNumbers: z.array(z.string()),
  from: z.string().nullable(),
  to: z.string().nullable(),
  departLocal: z.string().nullable(),
});

/**
 * `flight_rebooked`: the legacy back office rebooked a flight. The old voucher stays emitted
 * (tag REQUESTED_CHANGE) and a new one was issued; voucher-events detects the pair and
 * publishes this language-neutral photo of the NEW flight, and the conversation localizes it.
 * It rides the `trip_update` preference row.
 */
export const FlightRebookedMetadataSchema = RebookedFlightCardSchema.extend({
  tripId: z.string().min(1),
  /** The voucher issued by the rebooking. */
  voucherId: z.string().min(1),
  /** The voucher it replaced (still emitted). */
  replacesVoucherId: z.string().min(1),
  carrier: z.string().nullable(),
  /** The new flight numbers, in order ("1452"; "227", "759" across a connection). */
  flightNumbers: z.array(z.string()),
  /** IATA of the first boarding and last landing. */
  from: z.string().nullable(),
  to: z.string().nullable(),
  /** Offset-less wall clock `YYYY-MM-DDTHH:mm[:ss]` of the new departure. */
  departLocal: z.string().nullable(),
  /** The voucher origin channel (`purchase_origin`): decides the e-mail, as for `trip_update`. */
  originChannel: z.string().nullable().optional(),
  /**
   * The new flight as calendar events. Optional: producer and conversation deploy separately. A
   * malformed calendar reads as absent, so it never makes the whole notice unsendable.
   */
  calendar: z.array(TripCalendarEventSchema).optional().catch(undefined),
  /**
   * The replaced flight, struck through on the e-mail (#7099). A malformed one reads as absent:
   * the notice never becomes unsendable over it.
   */
  previousFlight: PreviousFlightSchema.optional().catch(undefined),
});
export type FlightRebookedMetadata = z.infer<typeof FlightRebookedMetadataSchema>;
